import L from "leaflet";

export const ORTHO_URL = "https://ags.cuzk.cz/arcgis1/rest/services/ORTOFOTO_WM/MapServer/tile/{z}/{y}/{x}";
export const KN_URL = "https://services.cuzk.cz/wms/wms.asp";
export const MAX_NATIVE = 20;
// ČÚZK terms: name the source as "ČÚZK – on-line", link the terms and the service metadata (shown in the Vrstvy sheet)
export const CUZK_TERMS = "https://cuzk.gov.cz/Predpisy/Podminky-poskytovani-prostor-dat-a-sitovych-sluzeb/Podminky-poskytovani-sitovych-sluzeb-CUZK.aspx";
export const CUZK_METADATA: [string, string][] = [
  ["Ortofoto ČR (Web Mercator)", "https://geoportal.gov.cz/php/micka/record/basic/CZ-CUZK-AGS-ORTOFOTO-MERCATOR"],
  ["Katastrální mapa (WMS)", "https://geoportal.gov.cz/php/micka/record/basic/CZ-00025712-CUZK_WMS-MD_KM"],
  ["Mapová služba nad daty RÚIAN (hranice parcel)", "https://geoportal.gov.cz/php/micka/record/basic/CZ-CUZK-AGS-RUIAN"],
];

export interface MapRotation {
  getBearing(): number;
  setBearing(deg: number): void;
  setLocked(locked: boolean): void;
  isLocked(): boolean;
  onChange(fn: (deg: number) => void): () => void;
  screenToContainer(clientX: number, clientY: number): L.Point;
  containerToScreen(pt: L.Point): L.Point;
  panByScreen(dx: number, dy: number): void;
}

function normDeg(deg: number) {
  let d = ((deg + 180) % 360 + 360) % 360 - 180;
  if (Object.is(d, -0) || Math.abs(d) < 0.05) d = 0;
  return d;
}

export function calcPhotoBounds(width?: number | null, height?: number | null) {
  const w = width && width > 0 ? width : 1600;
  const h = height && height > 0 ? height : 1200;
  const sc = 80 / Math.max(w, h);
  return L.latLngBounds([0, 0], [+(h * sc).toFixed(4), +(w * sc).toFixed(4)]);
}

export function createMap(el: HTMLElement, photoOpts?: { url?: string; width?: number | null; height?: number | null }) {
  const isPhoto = !!photoOpts;
  let bearing = 0; // Default: 0° = North up
  let locked = false;
  const listeners = new Set<(deg: number) => void>();

  const syncSize = () => {
    const parent = el.parentElement;
    if (!parent) return;
    const w = parent.clientWidth || window.innerWidth;
    const h = parent.clientHeight || window.innerHeight;
    const d = Math.ceil(Math.hypot(w, h)) + 4;
    el.style.position = "absolute";
    el.style.width = d + "px";
    el.style.height = d + "px";
    el.style.left = Math.round((w - d) / 2) + "px";
    el.style.top = Math.round((h - d) / 2) + "px";
  };
  syncSize();

  // no attribution on the map itself (little room on a phone); the source is credited in the Vrstvy sheet
  const map = L.map(el, {
    crs: isPhoto ? L.CRS.Simple : L.CRS.EPSG3857,
    zoomControl: false,
    maxZoom: isPhoto ? 7 : 22,
    minZoom: isPhoto ? 1 : 7,
    zoomSnap: 0.25,
    zoomDelta: 0.5,
    attributionControl: false,
    touchZoom: "center",
  });

  const onResize = () => { syncSize(); map.invalidateSize({ pan: false }); };
  window.addEventListener("resize", onResize);
  map.on("unload", () => window.removeEventListener("resize", onResize));

  // Oversized square container needs padding compensation in fitBounds so bounds fit the visible viewport
  const origFitBounds = map.fitBounds.bind(map);
  map.fitBounds = (bounds: L.LatLngBoundsExpression, opts?: L.FitBoundsOptions) => {
    const parent = el.parentElement;
    const ox = parent ? Math.max(0, Math.round((el.offsetWidth - parent.clientWidth) / 2)) : 0;
    const oy = parent ? Math.max(0, Math.round((el.offsetHeight - parent.clientHeight) / 2)) : 0;
    const ptl = L.point(opts?.paddingTopLeft ?? opts?.padding ?? [0, 0]);
    const pbr = L.point(opts?.paddingBottomRight ?? opts?.padding ?? [0, 0]);
    return origFitBounds(bounds, {
      ...opts,
      paddingTopLeft: [ptl.x + ox, ptl.y + oy],
      paddingBottomRight: [pbr.x + ox, pbr.y + oy],
    });
  };

  function screenCenter() {
    const r = (el.parentElement ?? el).getBoundingClientRect();
    return { cx: r.left + r.width / 2, cy: r.top + r.height / 2, w: r.width, h: r.height };
  }

  function screenToContainer(clientX: number, clientY: number): L.Point {
    const { cx, cy } = screenCenter();
    const dx = clientX - cx, dy = clientY - cy;
    const rad = (bearing * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    const u = dx * cos + dy * sin;
    const v = -dx * sin + dy * cos;
    return L.point(el.offsetWidth / 2 + u, el.offsetHeight / 2 + v);
  }

  function containerToScreen(pt: L.Point): L.Point {
    const { w, h } = screenCenter();
    const u = pt.x - el.offsetWidth / 2, v = pt.y - el.offsetHeight / 2;
    const rad = (bearing * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    return L.point(w / 2 + u * cos - v * sin, h / 2 + u * sin + v * cos);
  }

  function panByScreen(dx: number, dy: number) {
    const rad = (bearing * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    map.panBy([dx * cos + dy * sin, -dx * sin + dy * cos]);
  }

  map.mouseEventToContainerPoint = (e: MouseEvent | Touch) => screenToContainer(e.clientX, e.clientY);

  // Rotate drag delta so panning with 1 finger / mouse always follows screen direction
  const draggable = (map.dragging as any)?._draggable;
  if (draggable) {
    draggable.on("predrag", () => {
      if (!bearing || !draggable._lastEvent || !draggable._startPoint) return;
      const ev = draggable._lastEvent;
      const first = ev.touches && ev.touches[0] ? ev.touches[0] : ev;
      if (typeof first.clientX !== "number") return;
      const dx = first.clientX - draggable._startPoint.x;
      const dy = first.clientY - draggable._startPoint.y;
      const rad = (bearing * Math.PI) / 180;
      const cos = Math.cos(rad), sin = Math.sin(rad);
      draggable._newPos = draggable._startPos.add(L.point(dx * cos + dy * sin, -dx * sin + dy * cos));
    });
  }

  function setBearing(deg: number) {
    bearing = normDeg(deg);
    el.style.setProperty("--map-rot", bearing + "deg");
    for (const fn of listeners) fn(bearing);
  }

  // ---- Two-finger touch twist rotation (only when not placing a point) ----
  let touchStartAng: number | null = null;
  let touchStartBearing = 0;
  let touchRotating = false;

  el.addEventListener("touchstart", (e) => {
    if (locked || e.touches.length !== 2) { touchStartAng = null; touchRotating = false; return; }
    const t0 = e.touches[0], t1 = e.touches[1];
    touchStartAng = (Math.atan2(t1.clientY - t0.clientY, t1.clientX - t0.clientX) * 180) / Math.PI;
    touchStartBearing = bearing;
    touchRotating = false;
  }, { passive: true });

  el.addEventListener("touchmove", (e) => {
    if (locked || e.touches.length !== 2 || touchStartAng === null) return;
    const t0 = e.touches[0], t1 = e.touches[1];
    const ang = (Math.atan2(t1.clientY - t0.clientY, t1.clientX - t0.clientX) * 180) / Math.PI;
    let diff = normDeg(ang - touchStartAng);
    if (!touchRotating) {
      if (Math.abs(diff) < 4) return;
      touchRotating = true;
      touchStartAng = ang;
      touchStartBearing = bearing;
      diff = 0;
    }
    setBearing(touchStartBearing + diff);
  }, { passive: true });

  const endTouchRotate = () => {
    if (touchRotating && Math.abs(bearing) < 5) setBearing(0);
    touchStartAng = null;
    touchRotating = false;
  };
  el.addEventListener("touchend", endTouchRotate, { passive: true });
  el.addEventListener("touchcancel", endTouchRotate, { passive: true });

  // ---- Desktop mouse rotation: Right-button drag or Alt + left-button drag ----
  let mouseRotStartAng: number | null = null;
  let mouseRotStartBearing = 0;
  let mouseRotated = false;

  el.addEventListener("mousedown", (e) => {
    if (locked || !(e.button === 2 || (e.button === 0 && e.altKey))) return;
    e.preventDefault();
    const { cx, cy } = screenCenter();
    mouseRotStartAng = (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
    mouseRotStartBearing = bearing;
    mouseRotated = false;
    map.dragging.disable();
    const onMove = (ev: MouseEvent) => {
      if (mouseRotStartAng === null) return;
      const c = screenCenter();
      const ang = (Math.atan2(ev.clientY - c.cy, ev.clientX - c.cx) * 180) / Math.PI;
      const diff = normDeg(ang - mouseRotStartAng);
      if (Math.abs(diff) > 1) mouseRotated = true;
      setBearing(mouseRotStartBearing + diff);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (mouseRotated && Math.abs(bearing) < 4) setBearing(0);
      mouseRotStartAng = null;
      map.dragging.enable();
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  });
  el.addEventListener("contextmenu", (e) => { if (mouseRotated) { e.preventDefault(); mouseRotated = false; } });

  const rot: MapRotation = {
    getBearing: () => bearing,
    setBearing,
    setLocked(l: boolean) {
      locked = l;
      if (l) { touchStartAng = null; touchRotating = false; }
    },
    isLocked: () => locked,
    onChange(fn) { listeners.add(fn); fn(bearing); return () => listeners.delete(fn); },
    screenToContainer,
    containerToScreen,
    panByScreen,
  };

  const ortho = L.tileLayer(ORTHO_URL, {
    maxNativeZoom: MAX_NATIVE, maxZoom: 22, crossOrigin: "anonymous",
  });
  const kn = L.tileLayer.wms(KN_URL, {
    layers: "hranice_parcel_i,parcelni_cisla_i", format: "image/png", transparent: true, version: "1.3.0",
    minZoom: 16, maxNativeZoom: MAX_NATIVE, maxZoom: 22, crossOrigin: "anonymous", opacity: 0.9, tileSize: 512,
  } as L.WMSOptions);

  let photoOverlay: L.ImageOverlay | null = null;
  let photoBounds = calcPhotoBounds(photoOpts?.width, photoOpts?.height);

  function setPhotoOverlay(url: string, width?: number | null, height?: number | null) {
    photoBounds = calcPhotoBounds(width, height);
    if (photoOverlay) photoOverlay.remove();
    if (url) photoOverlay = L.imageOverlay(url, photoBounds, { interactive: false }).addTo(map);
    return photoBounds;
  }

  if (isPhoto) {
    if (photoOpts?.url) setPhotoOverlay(photoOpts.url, photoOpts.width, photoOpts.height);
    map.fitBounds(photoBounds, { paddingTopLeft: [16, 80], paddingBottomRight: [16, 100] });
  } else {
    ortho.addTo(map);
    let knOn = false;
    try { knOn = localStorage.getItem("zahrada:kn") === "1"; } catch {}
    if (knOn) kn.addTo(map);
    map.setView([49.8, 15.5], 7);
  }
  return { map, ortho, kn, rot, setPhotoOverlay, getPhotoBounds: () => photoBounds };
}

export type Fix = { lat: number; lng: number; acc: number; at: number };

export function startGps(map: L.Map, onFix?: (f: Fix) => void) {
  let marker: L.Marker | null = null, circle: L.Circle | null = null, last: Fix | null = null;
  let watchId: number | null = null;
  const start = () => {
    if (!("geolocation" in navigator) || watchId != null) return;
    watchId = navigator.geolocation.watchPosition((pos) => {
      last = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy, at: Date.now() };
      const ll: L.LatLngExpression = [last.lat, last.lng];
      if (!marker) {
        circle = L.circle(ll, { radius: last.acc, color: "#1a73e8", weight: 1, fillOpacity: 0.12, interactive: false }).addTo(map);
        marker = L.marker(ll, { icon: L.divIcon({ className: "", html: "<div class=\"gps-dot\"></div>", iconSize: [18, 18], iconAnchor: [9, 9] }), interactive: false, zIndexOffset: -100 }).addTo(map);
      } else { marker.setLatLng(ll); circle!.setLatLng(ll).setRadius(last.acc); }
      onFix?.(last);
    }, (err) => { if (err.code === err.PERMISSION_DENIED) { watchId = null; } }, { enableHighAccuracy: true, maximumAge: 3000, timeout: 30000 });
  };
  start();
  return {
    last: () => last,
    restart: start,
    stop: () => { if (watchId != null) navigator.geolocation.clearWatch(watchId); watchId = null; },
  };
}

/** Wait for a reasonably accurate GPS position (or the best one within the time limit). */
export function currentPosition(timeoutMs = 15000, wantAcc = 15): Promise<Fix> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) return reject(new Error("Tento prohlížeč neumí zjistit polohu."));
    let best: Fix | null = null;
    const done = (f: Fix | null, err?: string) => {
      navigator.geolocation.clearWatch(id); clearTimeout(t);
      if (f) resolve(f); else reject(new Error(err ?? "Polohu se nepodařilo zjistit."));
    };
    const id = navigator.geolocation.watchPosition((pos) => {
      const f = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy, at: Date.now() };
      if (!best || f.acc < best.acc) best = f;
      if (f.acc <= wantAcc) done(f);
    }, (err) => done(null, err.code === err.PERMISSION_DENIED ? "Přístup k poloze je zakázaný. Povol ho v nastavení prohlížeče." : undefined),
    { enableHighAccuracy: true, maximumAge: 0, timeout: timeoutMs });
    const t = setTimeout(() => done(best), timeoutMs);
  });
}

// ---------- offline tiles ----------
function tileRange(bounds: L.LatLngBounds, z: number) {
  const n = 2 ** z;
  const x = (lng: number) => Math.floor(((lng + 180) / 360) * n);
  const y = (lat: number) => Math.floor(((1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI) / 2) * n);
  return { x0: x(bounds.getWest()), x1: x(bounds.getEast()), y0: y(bounds.getNorth()), y1: y(bounds.getSouth()) };
}

export function tileUrls(kn: L.TileLayer.WMS, bounds: L.LatLngBounds, zmin: number, zmax: number) {
  const urls: string[] = [];
  for (let z = zmin; z <= zmax; z++) {
    const r = tileRange(bounds, z);
    for (let x = r.x0; x <= r.x1; x++) for (let y = r.y0; y <= r.y1; y++) {
      urls.push(L.Util.template(ORTHO_URL, { x, y, z }));
    }
    // katastr uses 512px tiles (fewer ČÚZK watermarks): its grid at zoom z matches the 256px grid at z-1
    if (z >= 16) {
      const k = tileRange(bounds, z - 1);
      for (let x = k.x0; x <= k.x1; x++) for (let y = k.y0; y <= k.y1; y++) urls.push(kn.getTileUrl(Object.assign(L.point(x, y), { z }) as L.Coords));
    }
  }
  return urls;
}

export async function prefetch(urls: string[], onProgress: (done: number, total: number, failed: number) => void) {
  const cache = await caches.open("cuzk-tiles");
  let done = 0, failed = 0, i = 0;
  const worker = async () => {
    while (i < urls.length) {
      const url = urls[i++];
      try {
        if (!(await cache.match(url))) {
          const r = await fetch(url, { mode: "cors", credentials: "omit" });
          if (r.ok) await cache.put(url, r); else failed++;
        }
      } catch { failed++; }
      done++;
      if (done % 5 === 0 || done === urls.length) onProgress(done, urls.length, failed);
    }
  };
  await Promise.all(Array.from({ length: 6 }, worker));
  onProgress(done, urls.length, failed);
  return { done, failed };
}
